import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrafficIcon from "@mui/icons-material/Traffic";
import TableChartIcon from "@mui/icons-material/TableChart";
import { MODAL_TYPES_SEMAFORO } from "../../config/MODAL_TYPES";

const SemaforoTopToolbar = ({
  onAction,
  envasado,
  kpis,
  onRefresh,
  loading,
}) => {
  const tipoLabel = envasado === 1 ? "Envasado" : "Granel";

  const kpiConfig = [
    {
      key: "rojo",
      label: "Críticos",
      color: "#dc2626",
      bg: "#fef2f2",
      border: "#fecaca",
    },
    {
      key: "amarillo",
      label: "Alerta",
      color: "#d97706",
      bg: "#fffbeb",
      border: "#fde68a",
    },
    {
      key: "blanco",
      label: "Al día",
      color: "#16a34a",
      bg: "#f0fdf4",
      border: "#bbf7d0",
    },
  ];

  return (
    <Box
      sx={{
        width: "97%",
        px: { xs: 2, sm: 3 },
        py: 2.5,
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 2, md: 0 },
        bgcolor: "white",
        borderRadius: 3,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      {/* Izquierda: Título y descripción */}
      <Stack spacing={0.5}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              background: " #5552f3 ",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: 20,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            <TrafficIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: { xs: 16, sm: 18 },
                color: "#0f172a",
                letterSpacing: "-0.3px",
                lineHeight: 1.2,
              }}
            >
              Semáforo de Clientes
            </Typography>
          </Box>
          <Chip
            label={tipoLabel}
            size="small"
            sx={{
              bgcolor: "#e0e7ff",
              color: "#4338ca",
              fontWeight: 700,
              fontSize: 11,
              height: 24,
              borderRadius: 3,
              ml: 0.5,
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            fontSize: 13,
            pl: 0.5,
          }}
        >
          Estado de la cuenta corriente con documentos contables pendientes
        </Typography>
      </Stack>

      {/* Derecha: KPIs + Acciones */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        {/* KPI Cards */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {kpiConfig.map((kpi) => (
            <Box
              key={kpi.key}
              sx={{
                bgcolor: kpi.bg,
                border: `1px solid ${kpi.border}`,
                borderRadius: 2,
                px: 1.8,
                py: 1,
                textAlign: "center",
                minWidth: 72,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 4px 12px ${kpi.border}80`,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: kpi.color,
                  lineHeight: 1,
                }}
              >
                {kpis[kpi.key] || 0}
              </Typography>
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: kpi.color,
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  mt: 0.3,
                  opacity: 0.85,
                }}
              >
                {kpi.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 1, ml: { md: 1 } }}>
          {/* Refresh */}
          <Tooltip title="Actualizar datos">
            <IconButton
              onClick={onRefresh}
              disabled={loading}
              sx={{
                bgcolor: "#f1f5f9",
                color: "#475569",
                borderRadius: 2,
                width: 40,
                height: 40,
                "&:hover": { bgcolor: "#e2e8f0", color: "#0f172a" },
              }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 20,
                  animation: loading ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>

          {/* Filtros */}
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={() =>
              onAction({ type: MODAL_TYPES_SEMAFORO.ABRIR_FILTROS })
            }
            sx={{
              bgcolor: "#0f172a",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              px: 2.5,
              py: 1,
              height: 40,
              boxShadow: "0 2px 8px rgba(15,23,42,0.2)",
              "&:hover": {
                bgcolor: "#1e293b",
                boxShadow: "0 4px 16px rgba(15,23,42,0.3)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Filtros
          </Button>
          <Button
            variant="contained"
            startIcon={<TableChartIcon />}
            onClick={() =>
              onAction({ type: MODAL_TYPES_SEMAFORO.EXPORTAR_EXCEL })
            }
            sx={{
              bgcolor: "#3a9c1c",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              px: 2.5,
              py: 1,
              height: 40,
              boxShadow: "0 2px 8px rgba(15,23,42,0.2)",
              "&:hover": {
                bgcolor: "#377c22",
                boxShadow: "0 4px 16px rgba(15,23,42,0.3)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Excel
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SemaforoTopToolbar;
